// ============================================================================
// FSA NOTE MODAL — DRIFT BADGE LIST
// ============================================================================
// Reusable inline alert list driven by the disclosure validation engine.
// Renders one chip per failing rule with severity color, message, and the
// conflicting values. Per the FSA audit-independence rule, drifts are
// ALWAYS warn-only — they never block save / submit / print. The auditor
// reviews drifts at submission time; the approver accepts or rejects
// each one during sign-off.
//
// Used by:
//   - DisclosureFixedGrid    (reconciliations — PY closing = CY opening, etc.)
//   - DisclosureVariableGrid (Authorised Capital, Shareholders > 5%, …)
//   - Future statement / report renderers
//
// The list is empty when no rules fail — caller can mount it
// unconditionally; it self-collapses when there's nothing to surface.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ValidationResult } from './validations';

interface Props {
  results: ValidationResult[];
  /** Optional title prefix when there are drifts ("Drift notifications" by default). */
  title?: string;
  /** Compact mode collapses to a single summary chip with hover-list. */
  compact?: boolean;
}

const DEFAULT_TITLE = 'Drift notifications';

export const DriftBadgeList: React.FC<Props> = ({ results, title = DEFAULT_TITLE, compact }) => {
  if (!results || results.length === 0) return null;

  if (compact) {
    const summary = `${results.length} drift${results.length === 1 ? '' : 's'}`;
    const worst: 'warning' | 'error' =
      results.some(r => r.severity === 'error') ? 'error' : 'warning';
    return (
      <div
        style={compactChipStyle(worst)}
        title={results.map(r => `• ${r.message} (${formatNum(r.leftValue)} vs ${formatNum(r.rightValue)})`).join('\n')}
      >
        <IconSystem.alert size={12} />
        <span>{summary}</span>
      </div>
    );
  }

  return (
    <div style={listStyle} role="status" aria-label={title}>
      {results.length > 1 && (
        <div style={titleStyle}>{title}</div>
      )}
      {results.map(r => (
        <div key={r.code} style={chipStyle(r.severity)} title={r.message}>
          <IconSystem.alert size={12} />
          <span>{r.message}</span>
          <span style={deltaStyle}>
            ({formatNum(r.leftValue)} vs {formatNum(r.rightValue)})
          </span>
        </div>
      ))}
    </div>
  );
};

const formatNum = (v: number): string =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ───── styles ───────────────────────────────────────────────────────────────

const listStyle: React.CSSProperties = {
  marginTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const titleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
};

const chipStyle = (severity: 'warning' | 'error'): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 6,
  background: severity === 'error' ? '#fee2e2' : '#fef3c7',
  color: severity === 'error' ? '#991b1b' : '#92400e',
  fontSize: '0.72rem',
  fontWeight: 500,
  border: `1px solid ${severity === 'error' ? '#fecaca' : '#fde68a'}`,
});

const compactChipStyle = (severity: 'warning' | 'error'): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 8px',
  borderRadius: 999,
  background: severity === 'error' ? '#fee2e2' : '#fef3c7',
  color: severity === 'error' ? '#991b1b' : '#92400e',
  fontSize: '0.7rem',
  fontWeight: 600,
  cursor: 'help',
});

const deltaStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.7rem',
  opacity: 0.85,
};

export default DriftBadgeList;
